import React, { useState } from 'react';
import { Button, Modal, Box, Typography, TextField, IconButton, InputAdornment } from '@mui/material';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { Close, LockOutlined, Visibility, VisibilityOff } from '@mui/icons-material';
interface ChangePasswordValues {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
}
interface ResetPasswordProps {
    open: boolean;
    handleSubmit:(values:ChangePasswordValues)=>void;
    onClose: () => void;
}

const fieldSx = {
  '& .MuiInputLabel-root, .MuiInputBase-root': { fontSize: '14px' },
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    background: '#fff',
    '& fieldset': { borderColor: '#D1D5DB' },
    '&:hover fieldset': { borderColor: '#1c7ed6' },
    '&.Mui-focused fieldset': { borderColor: '#1c7ed6' },
  },
};

const ChangePasswordModal: React.FC<ResetPasswordProps> = ({ open, onClose,handleSubmit }) => {
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '92%', sm: 440 },
                    bgcolor: '#fff',
                    borderRadius: '16px',
                    boxShadow: '0 24px 60px rgba(15, 55, 110, 0.18)',
                    p: { xs: 3, sm: 4 },
                    outline: 'none',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '10px',
                                bgcolor: '#EAF4FF',
                                color: '#1c7ed6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <LockOutlined sx={{ fontSize: 20 }} />
                        </Box>
                        <Typography sx={{ fontWeight: 700, color: '#0B2545', fontSize: 20 }}>
                            Change Password
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ color: '#6B7280' }}>
                        <Close fontSize="small" />
                    </IconButton>
                </Box>
                <Formik
                    initialValues={{ oldPassword: '', newPassword: '', confirmPassword: '' }}
                    validate={(values) => {
                        const errors: Record<string, string> = {};
                        if (!values.oldPassword) {
                            errors.oldPassword = 'Required';
                        }
                        if (!values.newPassword) {
                            errors.newPassword = 'Required';
                        }
                        if (!values.confirmPassword) {
                            errors.confirmPassword = 'Required';
                        } else if (values.newPassword !== values.confirmPassword) {
                            errors.confirmPassword = 'Passwords do not match';
                        }
                        return errors;
                    }}
                    onSubmit={(values, { resetForm }) => {
                        handleSubmit(values);
                        resetForm();
                        onClose();
                    }}
                >
                    {({ handleSubmit }) => (
                        <Form onSubmit={handleSubmit}>
                            <Box mb={2}>
                                <Field
                                    name="oldPassword"
                                    type={showOldPassword ? 'text' : 'password'}
                                    as={TextField}
                                    label="Old Password"
                                    fullWidth
                                    sx={fieldSx}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowOldPassword(!showOldPassword)}
                                                    edge="end"
                                                >
                                                    {showOldPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Box sx={{ color: '#FF5454', fontSize: '13px', mt: 0.5 }}>
                                    <ErrorMessage name="oldPassword" component="div" />
                                </Box>
                            </Box>
                            <Box mb={2}>
                                <Field
                                    name="newPassword"
                                    as={TextField}
                                    label="New Password"
                                    fullWidth
                                    sx={fieldSx}
                                    type={showNewPassword ? 'text' : 'password'}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                                    edge="end"
                                                >
                                                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Box sx={{ color: '#FF5454', fontSize: '13px', mt: 0.5 }}>
                                    <ErrorMessage name="newPassword" component="div" />
                                </Box>
                            </Box>
                            <Box mb={2.5}>
                                <Field
                                    name="confirmPassword"
                                    as={TextField}
                                    label="Confirm Password"
                                    fullWidth
                                    sx={fieldSx}
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    edge="end"
                                                >
                                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Box sx={{ color: '#FF5454', fontSize: '13px', mt: 0.5 }}>
                                    <ErrorMessage name="confirmPassword" component="div" />
                                </Box>
                            </Box>
                            <Box display="flex" justifyContent="flex-end" gap={1.2}>
                                <Button
                                    variant="outlined"
                                    onClick={onClose}
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: '10px',
                                        px: 2.5,
                                        fontWeight: 600,
                                        borderColor: '#D1D5DB',
                                        color: '#374151',
                                        '&:hover': { borderColor: '#9CA3AF', bgcolor: '#F9FAFB' },
                                    }}
                                >
                                    Close
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: '10px',
                                        px: 2.8,
                                        fontWeight: 600,
                                        bgcolor: '#1c7ed6',
                                        boxShadow: 'none',
                                        '&:hover': { bgcolor: '#1769b5', boxShadow: 'none' },
                                    }}
                                >
                                    Submit
                                </Button>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </Box>
        </Modal>
    );
};

export default ChangePasswordModal;
